<template>
  <div class="mobile-page">
    <div class="mobile-header haz-header">
      <div class="mode-tabs">
        <span class="mode-tab" :class="{ on: mode === 'reports' }" @click="switchMode('reports')">异常报告</span>
        <span class="mode-tab" :class="{ on: mode === 'closure' }" @click="switchMode('closure')">隐患闭环</span>
      </div>
      <p class="haz-count">共 {{ all.length }} 条 · 未关闭 {{ counts.open }} · 已关闭 {{ counts.closed }}</p>
    </div>

    <div class="mobile-body">
      <!-- 筛选 -->
      <div class="chip-row">
        <span
          v-for="c in statusChips" :key="c.value"
          class="chip" :class="{ on: statusSel === c.value }"
          @click="statusSel = c.value"
        >{{ c.label }}<template v-if="c.count !== undefined"> {{ c.count }}</template></span>
      </div>
      <div class="chip-row level-row">
        <span class="chip" :class="{ on: levelSel === '' }" @click="levelSel = ''">全部等级</span>
        <span v-for="lv in levelChips" :key="lv" class="chip" :class="{ on: levelSel === lv }" @click="levelSel = lv">{{ lv }}</span>
      </div>
      <div class="search-line">
        <el-input v-model="kw" placeholder="搜索描述 / 位置 / 上报人 / 编号" clearable size="default">
          <template #prefix><el-icon><Search /></el-icon></template>
        </el-input>
        <el-button size="default" @click="refresh" circle><el-icon><Refresh /></el-icon></el-button>
      </div>

      <div v-if="loading" class="center-box">
        <el-icon class="is-loading" :size="28"><Loading /></el-icon>
        <p class="sub-text">加载中…</p>
      </div>
      <div v-else-if="error" class="white-card center-box">
        <el-icon :size="36" color="#c5221f"><WarningFilled /></el-icon>
        <p class="sub-text">{{ error }}</p>
        <el-button size="small" type="primary" plain style="margin-top:10px" @click="load">重试</el-button>
      </div>
      <div v-else-if="list.length === 0" class="white-card center-box">
        <el-icon :size="42" color="#c0c4cc"><Warning /></el-icon>
        <p class="sub-text">没有符合条件的{{ mode === 'closure' ? '隐患' : '异常报告' }}</p>
      </div>

      <template v-else>
        <div class="hazard-card" v-for="h in list" :key="h.id" @click="open(h)">
          <div class="hc-top">
            <span class="hc-id mono">{{ h.id }}</span>
            <span class="hc-pills">
              <span v-if="h.level" class="pill" :class="levelPill(h.level)">{{ h.level }}</span>
              <span class="pill" :class="statusPill(h)">{{ h.closed ? '已关闭' : h.status }}</span>
            </span>
          </div>
          <div class="hc-desc">{{ h.description }}</div>
          <div class="hc-loc" v-if="h.location"><el-icon :size="12"><Location /></el-icon> {{ h.location }}</div>

          <!-- 闭环进度 -->
          <div v-if="mode === 'closure' || h.handle_log.length" class="hc-flow">
            <template v-for="(step, si) in flowSteps(h)" :key="si">
              <span class="flow-dot" :class="{ done: step.done, cur: step.cur }"></span>
              <span v-if="si < flowSteps(h).length - 1" class="flow-line" :class="{ done: flowSteps(h)[si + 1].done || flowSteps(h)[si + 1].cur }"></span>
            </template>
            <span class="flow-extra">{{ h.handle_log.length ? '已处置 ' + h.handle_log.length + ' 次' : '等待处置' }}</span>
          </div>

          <div class="hc-meta">
            <span class="hc-src">{{ h.source || '上报' }}</span>
            <span v-if="h.department">{{ h.department }}</span>
            <span v-if="h.reporter">· {{ h.reporter }}</span>
            <span v-if="h.photoCount">📷 {{ h.photoCount }}</span>
            <span class="hc-time">📅 {{ fmt(h.created_at) }}</span>
            <span v-if="mode === 'closure' && h.updated_at && h.updated_at !== h.created_at" class="hc-upd">更新 {{ fmt(h.updated_at) }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchHazards } from '../api'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()

const all = ref([])
const loading = ref(true)
const error = ref('')
const kw = ref('')
const statusSel = ref('')
const levelSel = ref('')

const mode = ref('reports')
if (route.query.mode === 'closure') mode.value = 'closure'
if (route.query.status === 'open') statusSel.value = '未关闭'
else if (route.query.status === 'closed') statusSel.value = '已关闭'

const counts = computed(() => {
  const out = { total: all.value.length, open: 0, closed: 0, 待处置: 0, 处置中: 0, 已完成: 0 }
  for (const h of all.value) {
    if (h.closed) out.closed++
    else out.open++
    const s = h.closed ? '已关闭' : h.status
    if (s === '待处置') out.待处置++
    else if (s === '处置中') out.处置中++
    else if (s === '已完成') out.已完成++
  }
  return out
})

const statusChips = computed(() => {
  const list = []
  if (mode.value === 'reports') {
    list.push({ value: '', label: '全部' })
    for (const s of ['待处置', '处置中', '已完成', '已关闭']) list.push({ value: s, label: s, count: counts.value[s] || 0 })
  } else {
    list.push({ value: '未关闭', label: '未关闭', count: counts.value.open })
    for (const s of ['处置中', '已完成']) list.push({ value: s, label: s, count: counts.value[s] || 0 })
    list.push({ value: '已关闭', label: '已关闭', count: counts.value.closed })
  }
  return list
})

const levelChips = computed(() => {
  const set = new Set()
  all.value.forEach(h => { if (h.level) set.add(h.level) })
  return Array.from(set).sort((a, b) => rankLv(a) - rankLv(b))
})

function rankLv(l) {
  const order = { 紧急: 0, 重大: 1, 较大: 2, 一般: 3 }
  return order[l] !== undefined ? order[l] : 9
}
function levelPill(l) {
  if (l === '紧急' || l === '重大') return 'pill-red'
  if (l === '较大') return 'pill-orange'
  if (l === '一般') return 'pill-blue'
  return 'pill-gray'
}
function statusPill(h) {
  if (h.closed) return 'pill-green'
  if (h.status === '处置中') return 'pill-orange'
  if (h.status === '已完成') return 'pill-purple'
  return 'pill-red'
}

const list = computed(() => {
  let arr = [...all.value]
  // 状态
  if (statusSel.value) {
    if (statusSel.value === '未关闭') arr = arr.filter(h => !h.closed)
    else if (statusSel.value === '已关闭') arr = arr.filter(h => h.closed)
    else arr = arr.filter(h => !h.closed && h.status === statusSel.value)
  }
  // 等级
  if (levelSel.value) arr = arr.filter(h => h.level === levelSel.value)
  // 关键字
  const k = kw.value.trim().toLowerCase()
  if (k) {
    arr = arr.filter(h => [h.id, h.description, h.location, h.reporter, h.department, h.source, h.group, h.item_name].join(' ').toLowerCase().includes(k))
  }
  // 排序
  if (mode.value === 'closure') {
    arr.sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || '')))
    arr.sort((a, b) => (a.closed ? 1 : 0) - (b.closed ? 1 : 0))
  } else {
    arr.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
  }
  return arr
})

function flowSteps(h) {
  const steps = ['待处置', '处置中', '已完成', '已关闭']
  const cur = h.closed ? '已关闭' : h.status
  return steps.map(s => ({
    label: s,
    done: cur === '已关闭' || (steps.indexOf(cur) > steps.indexOf(s)),
    cur: cur === s
  }))
}

function switchMode(m) {
  mode.value = m
  statusSel.value = ''
  router.replace({ query: { mode: m } })
}

function fmt(t) {
  return t ? dayjs(t).format('MM-DD HH:mm') : ''
}

function open(h) {
  router.push({ path: `/hazard/${h.id}`, state: { hazard: h } })
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const r = await fetchHazards({ all: 1, light: 1 })
    all.value = r.data || []
  } catch (e) {
    error.value = e.message || '加载失败'
  } finally {
    loading.value = false
  }
}

function refresh() { load() }

onMounted(load)
</script>

<style scoped>
.haz-header { padding-bottom: 12px; }
.mode-tabs {
  display: flex; background: rgba(255,255,255,.16); border-radius: 24px; padding: 3px;
  max-width: 320px; margin-bottom: 8px;
}
.mode-tab {
  flex: 1; text-align: center; padding: 6px 0; font-size: 14px; font-weight: 600;
  color: rgba(255,255,255,.9); border-radius: 20px; cursor: pointer; transition: all .2s;
}
.mode-tab.on { background: #fff; color: #1a73e8; }
.haz-count { font-size: 12px; opacity: .92; }

.level-row { margin-top: 2px; }
.search-line { display: flex; gap: 8px; margin-bottom: 10px; }
.search-line .el-input { flex: 1; }

.hazard-card {
  background: #fff; border-radius: 14px; padding: 13px 14px; margin-bottom: 10px;
  box-shadow: 0 3px 12px rgba(31,45,61,.06); cursor: pointer; transition: transform .15s;
}
.hazard-card:active { transform: scale(.99); }
.hc-top { display: flex; justify-content: space-between; align-items: center; gap: 6px; margin-bottom: 6px; }
.hc-id { font-size: 12.5px; font-weight: 700; color: #5f6368; }
.hc-pills { display: flex; gap: 5px; flex-wrap: wrap; justify-content: flex-end; }
.hc-desc {
  font-size: 14.5px; color: #202124; line-height: 1.55;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.hc-loc { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #5f6368; margin-top: 6px; }

.hc-flow { display: flex; align-items: center; margin-top: 10px; padding-top: 9px; border-top: 1px dashed #e8eaed; }
.flow-dot {
  width: 9px; height: 9px; border-radius: 50%; background: #e0e0e0; flex-shrink: 0;
  position: relative; z-index: 1;
}
.flow-dot.done { background: #34a853; }
.flow-dot.cur { background: #1a73e8; box-shadow: 0 0 0 3px rgba(26,115,232,.2); }
.flow-line { flex: 1; height: 2px; background: #e0e0e0; margin: 0 2px; }
.flow-line.done { background: #34a853; }
.flow-extra { margin-left: 8px; font-size: 11px; color: #9aa0a6; }

.hc-meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: 7px;
  font-size: 11.5px; color: #9aa0a6; margin-top: 8px;
}
.hc-src { background: #f1f3f4; color: #5f6368; border-radius: 8px; padding: 1px 8px; }
.hc-time { margin-left: auto; }
.hc-upd { color: #5f6368; }
</style>
